import { useCallback, useState } from 'react'

import { setEntryState } from '../actions/set-entry-state'
import { fetchEntry } from '../helpers/fetch-entry'
import { EntryOption, FetchErrorOption } from '../types'

import { useErrorObserver } from './useErrorObserver'

import { Entries } from '@/constants/content-stack'
import { FetchStatus } from '@/constants/general'
import { sanitizeHtml } from '@/services/content-stack/helpers/sanitize-html'
export function useFetchEntry<RawEntry, Entry>({
  entryParams,
  parser,
  onError,
}: {
  entryParams: EntryOption['entryParams']
  parser: (rawEntry: RawEntry) => Entry
  onError: FetchErrorOption<RawEntry, Entry>['onError']
}) {
  /**
   * The entryDispatched state is used to pass to the Error observer.
   * Only the instance that dispatches the fetch callback can watch the errors of the request
   * Once the entryDispatched is set, the errorObserver will start to watch it.
   */
  const [entryDispatched, setEntryDispatched] = useState<
    undefined | Entries[keyof Entries]
  >(undefined)

  useErrorObserver(entryDispatched, onError)

  return useCallback(
    (entryOptions?: Omit<EntryOption, 'entryParams'>) => {
      setEntryDispatched(entryParams)
      setEntryState(entryParams, FetchStatus.FETCHING)

      fetchEntry<RawEntry>({
        entryParams,
        query: entryOptions?.query || [],
      })
        .then((rawEntry) => {
          setEntryState(
            entryParams,
            FetchStatus.SUCCESS,
            parser(sanitizeHtml(rawEntry)),
          )
        })
        .catch((error) => {
          setEntryState(entryParams, FetchStatus.ERROR, error)
        })
    },
    [entryParams, parser],
  )
}
